import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { environment } from '../../../environments/environment';

export type S3UploadFolder = 'profileImage' | 'familyLogo' | 'memberPhoto' | 'storymedia' | 'storyvideo' | 'storyaudio' | 'storyaudioimage';

@Injectable({ providedIn: 'root' })
export class S3ImageUploadService {
  private platformId = inject(PLATFORM_ID);

  private folderFor(type: S3UploadFolder): string {
    switch (type) {
      case 'profileImage':    return environment.S3_BUCKET_FOLDER_PROFILE_IMAGE;
      case 'familyLogo':      return environment.S3_BUCKET_FOLDER_FAMILY_LOGO;
      case 'memberPhoto':     return environment.S3_BUCKET_FOLDER_MEMBER_PHOTO;
      case 'storymedia':      return environment.S3_BUCKET_FOLDER_STORY_MEDIA;
      case 'storyvideo':      return environment.S3_BUCKET_FOLDER_STORY_VIDEO;
      case 'storyaudio':      return environment.S3_BUCKET_FOLDER_STORY_AUDIO;
      case 'storyaudioimage': return environment.S3_BUCKET_FOLDER_STORY_AUDIO_IMAGE;
    }
  }

  /**
   * Upload a file to the configured S3 bucket using a pre-signed PUT URL.
   * The backend should provide the pre-signed URL; this method performs the
   * actual binary upload and returns the public object URL.
   *
   * Usage pattern:
   *  1. Call your API to get a pre-signed URL for the desired key.
   *  2. Pass it to uploadViaPresignedUrl() along with the File.
   *  3. Store the returned public URL in your form/state.
   */
  async uploadViaPresignedUrl(presignedUrl: string, file: File): Promise<string> {
    const resp = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!resp.ok) {
      throw new Error(`S3 upload failed: ${resp.status} ${resp.statusText}`);
    }

    // Strip query string to return the clean public URL
    return presignedUrl.split('?')[0];
  }

  /**
   * Build the public S3 object URL for a given key (file name).
   * Useful when the API returns just a filename and you need the full URL
   * to display images.
   */
  buildPublicUrl(folder: S3UploadFolder, fileName: string): string {
    const region = environment.S3_REGION;
    const bucket = environment.S3_BUCKET_NAME;
    const key = `${this.folderFor(folder)}/${fileName}`;
    return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
  }

  /**
   * Check whether S3 is configured in this environment.
   */
  isConfigured(): boolean {
    return !!(environment.S3_BUCKET_NAME && environment.S3_REGION);
  }

  /**
   * Resolve the S3 folder for a given story media type.
   *   - video       → storyvideo/
   *   - audio       → storyaudio/
   *   - image-audio → storyaudioimage/
   *   - fallback    → storymedia/
   */
  private storyFolderFor(mediaType: string): string {
    const base = this.folderFor('storymedia');
    switch (mediaType.toLowerCase()) {
      case 'video':       return `${base}/${this.folderFor('storyvideo')}`;
      case 'audio':       return `${base}/${this.folderFor('storyaudio')}`;
      case 'image-audio':
      case 'image+audio': return `${base}/${this.folderFor('storyaudioimage')}`;
      default:            return base;
    }
  }

  /**
   * Upload a story media file (video/audio/image) from a blob URL to the
   * appropriate S3 folder based on mediaType. Returns only the generated
   * filename so it can be passed directly to the create-story API.
   */
  async uploadStoryMedia(blobUrl: string, mediaType: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('S3 upload is only supported in the browser.');
    }

    const resp = await fetch(blobUrl);
    const blob = await resp.blob();

    const mimeToExt: Record<string, string> = {
      'video/webm': 'webm',
      'video/mp4':  'mp4',
      'audio/mp4':  'm4a',   // Safari/iOS native AAC recording
      'audio/webm': 'm4a',   // Chrome/Firefox opus-in-webm → save as .m4a
      'audio/mpeg': 'mp3',
      'audio/ogg':  'ogg',
      'image/jpeg': 'jpg',
      'image/png':  'png',
    };

    const lowerType = mediaType.toLowerCase();
    const ext = mimeToExt[blob.type]
      ?? (lowerType === 'audio' ? 'm4a' : lowerType === 'image+audio' || lowerType === 'image-audio' ? 'jpg' : 'webm');

    const fileName = `${crypto.randomUUID()}.${ext}`;
    const folder   = this.storyFolderFor(mediaType);
    const key      = `${folder}/${fileName}`;

    const client = new S3Client({
      region: environment.S3_REGION,
      credentials: {
        accessKeyId:     environment.S3_ACCESS_KEY_ID,
        secretAccessKey: environment.S3_SECRET_ACCESS_KEY,
      },
    });

    const arrayBuffer = await blob.arrayBuffer();
    await client.send(new PutObjectCommand({
      Bucket:      environment.S3_BUCKET_NAME,
      Key:         key,
      Body:        new Uint8Array(arrayBuffer),
      ContentType: blob.type || 'application/octet-stream',
    }));

    return fileName;
  }
}

